export interface TrimWindowIndices {
  startIndex: number;
  endIndex: number;
}

/**
 * Timestamps → frame indices for a demonstration's trim window.
 *
 * The runner picks the frames it replays with this, and the review UI windows
 * the previewed path with it, so a preview cannot select frames the runner
 * would not.
 *
 * Throws when the window selects no frames, rather than degrading to a
 * one-frame move: a routine configured with a window outside its recording
 * should fail loudly. Callers that can't fail (a preview) catch and show
 * nothing instead.
 *
 * An explicit trim overrides the idle-trim motion bound on that side only, so a
 * null side falls back to the recording's own motion span.
 */
export function computeTrimWindow(
  frames: ReadonlyArray<{ timestamp: number }>,
  trimStart: number | null,
  trimEnd: number | null,
  firstMotionTs: number | null,
  lastMotionTs: number | null,
): TrimWindowIndices {
  const effectiveStart = trimStart ?? firstMotionTs; // null ⇒ from frame 0
  const effectiveEnd = trimEnd ?? lastMotionTs; //      null ⇒ to last frame

  let startIndex = 0;
  let endIndex = frames.length - 1;

  if (effectiveStart !== null) {
    // No frame reaches effectiveStart ⇒ the window is empty on this side;
    // frames.length (past the last valid index) forces the empty-window guard
    // below rather than silently falling back to startIndex=0.
    const i = frames.findIndex((f) => f.timestamp >= effectiveStart);
    startIndex = i >= 0 ? i : frames.length;
  }

  if (effectiveEnd !== null) {
    // Symmetric miss case: no frame is old enough ⇒ -1 forces the same guard
    // rather than silently falling back to endIndex=frames.length-1.
    endIndex = -1;

    for (let i = frames.length - 1; i >= 0; i -= 1) {
      if (frames[i]!.timestamp <= effectiveEnd) {
        endIndex = i;
        break;
      }
    }
  }

  if (startIndex > endIndex) {
    throw new Error(
      `RunDemonstration trim window [${effectiveStart ?? 'start'}, ` +
        `${effectiveEnd ?? 'end'}] selects no recorded frames.`,
    );
  }

  return { startIndex, endIndex };
}
